import { Badge, Spin } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { MemoizedEcgChart } from '../../../../components/Charts/EcgChart'
import LiveDataChart from '../../../../components/Charts/LiveDataChart'
import { ecgSocket, socket } from '../../../../helpers/Socket'
import { convertUTCToLocal } from '../../../../helpers/Timezone'
import { useGetPatientAdmissionsMewsScoreDataQuery } from '../../../../state/patientAdmissionMews/api'
import {
  BpChartData,
  EcgSocketData,
  MewsChartData,
  PrChartData,
  RespChartData,
  Spo2ChartData,
  TempChartData,
  VitalsSocketData,
} from '../../../../types/SensorType'
import { TabEmptyText } from '../../PatientsViewStyle'
import {
  EcgChartWrapper,
  EcgChartsContainer,
  LiveDataTabContainer,
  LiveDataTabStatus,
  LiveDataTabStatusContent,
  LiveDataTabStatusLabel,
  LoadingContainer,
  LoadingContent,
  LoadingParagraph,
  LoadingText,
  MewsChartsContainer,
} from './LiveDataTabStyle'

type Props = {
  patientKey: string
}

export default function LiveDataTab({ patientKey }: Props) {
  const [isMewsConnected, setIsMewsConnected] = useState(socket.connected)
  const [isEcgConnected, setIsEcgConnected] = useState(ecgSocket.connected)
  const [mewsChartData, setMewsChartData] = useState<MewsChartData[]>([])
  const [spo2ChartData, setSpo2ChartData] = useState<Spo2ChartData[]>([])
  const [prChartData, setPrChartData] = useState<PrChartData[]>([])
  const [tempChartData, setTempChartData] = useState<TempChartData[]>([])
  const [respChartData, setRespChartData] = useState<RespChartData[]>([])
  const [bpChartData, setBpChartData] = useState<BpChartData[]>([])
  const [ecgChartData, setEcgChartData] = useState<number[]>([])
  const [ecgTime, setEcgTime] = useState('')
  const [isLoadingEcg, setIsLoadingEcg] = useState<boolean>(true)
  const [isDetected, setIsDetected] = useState(false)

  const { sensorAllocationID, mewsScore, createTimestamp } =
    useGetPatientAdmissionsMewsScoreDataQuery(
      {},
      {
        selectFromResult: ({ data }) => {
          const mewsScoreDatas = data?.mewsScores
          const currentMewsScoreData = mewsScoreDatas?.find(
            (data) => data.patientKey === patientKey
          )
          return { ...currentMewsScoreData }
        },
      }
    )

  const handleMewsData = useCallback(
    (data: VitalsSocketData) => {
      if (data.sakey === sensorAllocationID) {
        const time = data.time

        if (data.spo2 !== undefined && parseInt(data.spo2) !== 0) {
          const spo2 = parseInt(data.spo2)
          const spo2Score = spo2 > 89 ? 0 : 3

          setSpo2ChartData((prev) => {
            const updatedData = [...prev, { spo2, time, spo2Score }].sort(
              (a, b) => sortTimeAsc(a.time, b.time)
            )

            return updatedData.slice(-15)
          })
        }

        if (data.pr !== undefined && parseInt(data.pr) !== 0) {
          const pr = parseInt(data.pr)
          const prScore = pr > 40 && pr < 109 ? 0 : 3

          setPrChartData((prev) => {
            const updatedData = [...prev, { pr, time, prScore }].sort((a, b) =>
              sortTimeAsc(a.time, b.time)
            )

            return updatedData.slice(-15)
          })
        }

        if (data.temp !== undefined && parseFloat(data.temp) > 34) {
          const tempCelsius = parseFloat(data.temp)
          const tempFahrenheit = parseFloat(
            (tempCelsius * (9 / 5) + 32).toFixed(2)
          )
          const tempScore =
            tempFahrenheit >= 95.0 && tempFahrenheit <= 102.0 ? 0 : 3

          setTempChartData((prev) => {
            const updatedData = [
              ...prev,
              { temp: tempFahrenheit, time, tempScore },
            ].sort((a, b) => sortTimeAsc(a.time, b.time))

            return updatedData.slice(-50)
          })
        }

        if (data.resp !== undefined && parseInt(data.resp) !== 0) {
          const resp = parseInt(data.resp)
          const respScore = resp > 9 && resp < 25 ? 0 : 3

          setRespChartData((prev) => {
            const updatedData = [...prev, { resp, time, respScore }].sort(
              (a, b) => sortTimeAsc(a.time, b.time)
            )
            return updatedData.slice(-50)
          })
        }

        if (data.sys !== undefined && parseInt(data.sys) !== 0) {
          const sys = parseInt(data.sys)
          const dia = parseInt(data.dia)
          const bpScore = sys > 100 && sys < 199 ? 0 : 3

          setBpChartData((prev) => {
            const updatedData = [...prev, { sys, time, bpScore, dia }].sort(
              (a, b) => sortTimeAsc(a.time, b.time)
            )

            return updatedData.slice(-100)
          })
        }
      } else {
        setIsMewsConnected(false)
      }
    },
    [sensorAllocationID]
  )

  const handleEcgData = useCallback((data: EcgSocketData) => {
    setIsLoadingEcg(false)
    setEcgChartData(data.signals.map(({ value }) => value))
    setEcgTime(convertUTCToLocal(data.time))
    setIsDetected(data.detected)
  }, [])

  useEffect(() => {
    socket.connect()
    socket.on('connect', () => setIsMewsConnected(true))
    socket.on('disconnect', () => setIsMewsConnected(false))

    ecgSocket.connect()
    ecgSocket.on('connect', () => setIsEcgConnected(true))
    ecgSocket.on('disconnect', () => setIsEcgConnected(false))

    return () => {
      socket.disconnect()
      ecgSocket.disconnect()
    }
  }, [])

  useEffect(() => {
    if (sensorAllocationID) {
      if (isMewsConnected) {
        socket.emit('start', sensorAllocationID)
        socket.on('plot', handleMewsData)
      }
      if (isEcgConnected) {
        ecgSocket.emit('start', sensorAllocationID)
        ecgSocket.on('ecg-plot', handleEcgData)
        setTimeout(() => {
          setIsLoadingEcg(false)
        }, 60000)
      }
    }

    return () => {
      socket.off('plot', handleMewsData)
      ecgSocket.off('ecg-plot', handleEcgData)
    }
  }, [
    handleEcgData,
    handleMewsData,
    isEcgConnected,
    isMewsConnected,
    sensorAllocationID,
  ])

  useEffect(() => {
    if (createTimestamp && mewsScore !== undefined) {
      setMewsChartData((prev) => {
        const isActualNewData =
          prev.length === 0 || prev[prev.length - 1].time !== createTimestamp

        if (isActualNewData) {
          const updatedData = [
            ...prev,
            { mews: mewsScore, time: createTimestamp },
          ]

          return updatedData.slice(-15)
        }

        return prev
      })
    }
  }, [createTimestamp, mewsScore])

  if (!sensorAllocationID) return <TabEmptyText>Device Offline</TabEmptyText>

  return (
    <LiveDataTabContainer>
      <LiveDataTabStatus>
        <LiveDataTabStatusLabel>Online Status:</LiveDataTabStatusLabel>
        <LiveDataTabStatusContent>
          {isMewsConnected || isEcgConnected ? 'Online' : 'Offline'}
        </LiveDataTabStatusContent>
      </LiveDataTabStatus>
      <MewsChartsContainer>
        <LiveDataChart
          liveData={mewsChartData}
          lineChartDataKey='mews'
          lineChartDataName='mews'
        />
        <LiveDataChart
          liveData={spo2ChartData}
          lineChartDataKey='spo2'
          lineChartDataName='spo2'
        />
        <LiveDataChart
          liveData={prChartData}
          lineChartDataKey='pr'
          lineChartDataName='pr'
        />
        <LiveDataChart
          liveData={tempChartData}
          lineChartDataKey='temp'
          lineChartDataName='temp'
        />
        <LiveDataChart
          liveData={respChartData}
          lineChartDataKey='resp'
          lineChartDataName='resp'
        />
        <LiveDataChart
          liveData={bpChartData}
          lineChartDataName='bp'
          multiLineChartKey={['sys', 'dia']}
        />
      </MewsChartsContainer>

      {isLoadingEcg && (
        <>
          <LiveDataTabStatus>
            <LiveDataTabStatusLabel>ECG :</LiveDataTabStatusLabel>
            <LiveDataTabStatusContent>
              <Badge status='processing' text='Loading...' />
            </LiveDataTabStatusContent>
          </LiveDataTabStatus>
          <LoadingContainer>
            <LoadingContent>
              <Spin size='large' />
              <LoadingText>Loading ECG Signals...</LoadingText>
              <LoadingParagraph>
                It might take 30 to 60 seconds to receive the next batch of ECG
                live data.
              </LoadingParagraph>
            </LoadingContent>
          </LoadingContainer>
        </>
      )}

      {!isLoadingEcg && !ecgChartData.length && (
        <LiveDataTabStatus>
          <LiveDataTabStatusLabel>ECG :</LiveDataTabStatusLabel>
          <LiveDataTabStatusContent>NO DATA</LiveDataTabStatusContent>
        </LiveDataTabStatus>
      )}

      {!isLoadingEcg && !!ecgChartData.length && (
        <>
          <LiveDataTabStatus>
            <LiveDataTabStatusLabel>ECG :</LiveDataTabStatusLabel>
            <LiveDataTabStatusContent>
              {isDetected ? (
                <Badge status='warning' text='Arrhythmia Detected' />
              ) : (
                <Badge status='success' text='Normal' />
              )}
            </LiveDataTabStatusContent>
            <LiveDataTabStatusLabel style={{ marginLeft: 'auto' }}>
              Time:
            </LiveDataTabStatusLabel>
            <LiveDataTabStatusContent>{ecgTime}</LiveDataTabStatusContent>
          </LiveDataTabStatus>
          <EcgChartsContainer>
            <EcgChartWrapper>
              <MemoizedEcgChart
                signals={ecgChartData}
                offset={0}
                duration={20}
              />
            </EcgChartWrapper>
            <EcgChartWrapper>
              <MemoizedEcgChart
                signals={ecgChartData}
                offset={20}
                duration={20}
              />
            </EcgChartWrapper>
            <EcgChartWrapper>
              <MemoizedEcgChart
                signals={ecgChartData}
                offset={40}
                duration={20}
              />
            </EcgChartWrapper>
          </EcgChartsContainer>
        </>
      )}
    </LiveDataTabContainer>
  )
}

function sortTimeAsc(a: string, b: string): number {
  const timeA = new Date(a).getTime()
  const timeB = new Date(b).getTime()

  if (isNaN(timeA) || isNaN(timeB)) {
    throw new Error('Invalid date string provided')
  }

  return timeA - timeB
}
